// cutscene.ts ↔ game/CutScene.cpp — the mission cutscene/briefing player: the
// CutScene() entry the CutSceneAction trigger performs (TriggerActions.cpp:1220).
// The canvas CutSceneForm was removed (canvas-removal R1); the DOM cutscene
// overlay (dom/ingame/...) renders the briefing now, fed by the domCutScene
// seam. The text is passed RAW (the cutscene does not ExpandVars, unlike the
// E-Com) and stays SP-paused/queued via EnqueueStoryModal exactly as before.

import type { ShellEnv } from './shellform.ts';
import { EnqueueStoryModal } from './simpause.ts';

// game/res.h:44, 376
export const kidfCutScene = 1047;
export const kidcBitmap = 1002;

// game/soundeffects.h — ksfxHappyEnding (the <snd n> base index)
export const ksfxHappyEnding = 181;

/** The (raw) cutscene text the DOM overlay renders — the briefing string the
 *  CutSceneAction trigger performs, with its inline `<img NAME>`/`<snd N>` tags +
 *  the @-speaker narration. Unlike the E-Com, the cutscene does NOT ExpandVars the
 *  text — so the DOM path passes it through unchanged for display parity. */
export interface CutSceneInfo {
  /** the raw cutscene text (tags + narration). */
  message: string;
}

/** The in-mission CUTSCENE overlay seam (kidfCutScene 1047). CutScene() branches
 *  here INSIDE its EnqueueStoryModal show callback (so it stays queued + SP-paused).
 *  `done()` is the post-modal return point the host calls on dismiss (it unmounts
 *  the overlay + releases the queue's next()). */
export type CutSceneDomHook = (info: CutSceneInfo, done: () => void) => void;

/** The C++ globals CutScene() reaches: the ShellEnv bundle (gpmfrmm/gpiniForms/
 *  gtimm/gsndm) + gsim.Pause/gfMultiplayer through env.sim/env.fMultiplayer. Plus the
 *  cutscene overlay seam (domCutScene). */
export type CutSceneEnv = Pick<
  ShellEnv,
  'mfrmm' | 'gtimm' | 'sndm' | 'GetPlayfieldSize' | 'getSafeAreas' | 'evm' | 'sim' | 'fMultiplayer'
> & {
  /** the in-mission cutscene overlay seam (Phase 5). */
  domCutScene?: CutSceneDomHook;
};

// game/CutScene.cpp:34-49 — CutScene (the CutSceneAction::Perform UI arm).
// done = the C++ post-DoModal `delete pfrm` return point.
//
// The C++ call BLOCKS with gsim.Pause held around it (CutScene.cpp:40-46) —
// EnqueueStoryModal (simpause.ts) carries the one-at-a-time sequencing and
// the pause across any story modals raised by the same trigger pass.
export function CutScene(
  env: CutSceneEnv,
  pszText: string,
  fPauseSimulation: boolean,
  done: (() => void) | null = null,
): void {
  EnqueueStoryModal(env, fPauseSimulation, (next) => {
    // render the cutscene as a DOM overlay over the live canvas — INSIDE the show
    // callback, so it stays QUEUED + SP-PAUSED exactly like the canvas form did (the
    // EnqueueStoryModal wrapping is unchanged). The text is passed RAW (the cutscene
    // does not ExpandVars, unlike the E-Com). DISPLAY-only — no sim/command/wire change.
    if (env.domCutScene !== undefined) {
      env.domCutScene({ message: pszText }, () => {
        if (done !== null) done();
        next(); // release the pause hold + show the next queued story modal
      });
      return;
    }

    // No DOM cutscene seam wired (e.g. a headless harness) — release the queue.
    if (done !== null) done();
    next();
  });
}
